import { Check, Close } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { FormikErrors, useFormikContext } from 'formik';
import {
  AccordionCivilUnion,
  AccordionMarriage,
  NewPersonVM,
  ParentVM,
} from 'shell/components/shared/sipo-person-form/new-person';
import { memo, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { noop } from 'shell/utils/noop';

interface ButtonsProps {
  handleExit: () => void;
  disabled?: boolean;
  handleStep?: (id: number) => void;
  activeStep?: number;
  isEdit?: boolean;
  withProvenienza?: boolean;
}

const ButtonsComponent = ({
  handleExit,
  handleStep = noop,
  activeStep,
  isEdit = false,
  withProvenienza = false,
}: ButtonsProps): React.ReactElement => {
  const formContext = useFormikContext<NewPersonVM>();

  // errors
  const errorAnagrafe = useMemo(() => {
    return Boolean(
      formContext.errors.generality ||
        formContext.errors.subjectAttributes ||
        formContext.errors.birthdayDocument ||
        formContext.errors.citizenship ||
        (formContext.errors.paternity as FormikErrors<ParentVM>)?.generality ||
        (formContext.errors.maternity as FormikErrors<ParentVM>)?.generality ||
        false
    );
  }, [
    formContext.errors.generality,
    formContext.errors.subjectAttributes,
    formContext.errors.birthdayDocument,
    formContext.errors.citizenship,
    formContext.errors.paternity,
    formContext.errors.maternity,
  ]);

  const errorStatoCivile = useMemo(() => {
    return Boolean(
      formContext.errors.civilStatus ||
        (formContext.errors.marriage as FormikErrors<AccordionMarriage>)?.generality ||
        (formContext.errors.civilUnion as FormikErrors<AccordionCivilUnion>)?.generality ||
        formContext.errors.widowhood ||
        formContext.errors.voidCivilUnion ||
        formContext.errors.voidMarriage ||
        false
    );
  }, [
    formContext.errors.civilStatus,
    formContext.errors.marriage,
    formContext.errors.civilUnion,
    formContext.errors.widowhood,
    formContext.errors.voidCivilUnion,
    formContext.errors.voidMarriage,
  ]);

  const errorAltriDati = useMemo(() => {
    return Boolean(formContext.errors.professionTitle || false);
  }, [formContext.errors.professionTitle]);

  const errorProvenienza = useMemo(() => {
    return Boolean(formContext.errors.residenceOrigin || false);
  }, [formContext.errors.residenceOrigin]);

  return (
    <StickyDiv>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <StepButton
            active={0 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(0)}
          >
            <StepTypography active={0 === activeStep}>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Anagrafe &nbsp;
                {errorAnagrafe ? (
                  <Close color={0 === activeStep ? 'inherit' : 'error'} />
                ) : (
                  <Check color={0 === activeStep ? 'inherit' : 'success'} />
                )}
              </span>
            </StepTypography>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <StepButton
            active={1 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(1)}
            disabled
          >
            <StepTypography active={1 === activeStep} disabled>
              Residenza
            </StepTypography>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <StepButton
            active={2 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(2)}
          >
            <StepTypography active={2 === activeStep}>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Stato Civile &nbsp;
                {errorStatoCivile ? (
                  <Close color={2 === activeStep ? 'inherit' : 'error'} />
                ) : (
                  <Check color={2 === activeStep ? 'inherit' : 'success'} />
                )}
              </span>
            </StepTypography>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <StepButton
            active={3 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(3)}
            disabled
          >
            <StepTypography active={3 === activeStep} disabled>
              Famiglia
            </StepTypography>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <StepButton
            active={4 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(4)}
            disabled
          >
            <StepTypography active={4 === activeStep} disabled>
              Documenti
            </StepTypography>
          </StepButton>
        </Grid>
        <Grid item xs={12}>
          <StepButton
            active={5 === activeStep}
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => handleStep(5)}
          >
            <StepTypography active={5 === activeStep}>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Altri Dati &nbsp;
                {errorAltriDati ? (
                  <Close color={5 === activeStep ? 'inherit' : 'error'} />
                ) : (
                  <Check color={5 === activeStep ? 'inherit' : 'success'} />
                )}
              </span>
            </StepTypography>
          </StepButton>
        </Grid>
        {withProvenienza ? (
          <Grid item xs={12}>
            <StepButton
              active={6 === activeStep}
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => handleStep(6)}
            >
              <StepTypography active={6 === activeStep}>
                <span style={{ display: 'flex', justifyContent: 'center' }}>
                  Provenienza &nbsp;
                  {errorProvenienza ? (
                    <Close color={6 === activeStep ? 'inherit' : 'error'} />
                  ) : (
                    <Check color={6 === activeStep ? 'inherit' : 'success'} />
                  )}
                </span>
              </StepTypography>
            </StepButton>
          </Grid>
        ) : undefined}
      </Grid>
      <div>
        <div className="pmi-pt-3">
          <PmiButton variant="outlined" color="cdmBrandThird" fullWidth type="submit">
            {isEdit ? 'Conferma Modifica' : 'Conferma Registrazione'}
          </PmiButton>
        </div>
        <div className="pmi-pt-3">
          <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
            Annulla Modifiche
          </PmiButton>
        </div>
      </div>
    </StickyDiv>
  );
};

ButtonsComponent.displayName = 'Sipo Person Step Buttons';

const Buttons = memo(ButtonsComponent);

export default Buttons;
